import React, { useState } from "react";
import { Bet, useUpdateBetResultMutation } from "@/api/betting";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { PersonaAvatar } from "@/components/PersonaAvatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getStatusBadge, getValidationBadge } from "./StatusBadges";
import { formatDate } from "./utils/formatHelpers";
import BetDetailsModal from "./modals/BetDetailsModal";
import UpdateResultModal from "./modals/UpdateResultModal";
import { CheckCircle } from "lucide-react";

interface BetTableProps {
  bets: Bet[];
  personas: any[];
  selectedPersonaId?: string;
}

const BetTable: React.FC<BetTableProps> = ({
  bets,
  personas,
  selectedPersonaId,
}) => {
  const { toast } = useToast();
  const updateBetResult = useUpdateBetResultMutation();
  const [selectedBet, setSelectedBet] = useState<Bet | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [resultAction, setResultAction] = useState<"won" | "lost">("won");

  // Handle showing result update modal
  const handleUpdateResult = (bet: Bet, status: "won" | "lost") => {
    setSelectedBet(bet);
    setResultAction(status);
    setShowResultModal(true);
  };

  // Function to mark bet with specific result
  const markBetAs = (resultText: string = "") => {
    if (!selectedBet) return;

    // Use bet's persona ID or the selected one
    const effectivePersonaId = selectedPersonaId || selectedBet.persona_id;

    updateBetResult.mutate(
      {
        persona_id: effectivePersonaId,
        bet_id: selectedBet.id,
        status: resultAction,
        result: {
          updated_by: "user",
          updated_at: new Date().toISOString(),
          result_text: resultText || undefined,
        },
      },
      {
        onSuccess: () => {
          toast({
            description: `Bet marked as ${resultAction}`,
          });
          setShowResultModal(false);
          setSelectedBet(null);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            description: `Failed to update bet result: ${error.message}`,
          });
        },
      },
    );
  };

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Agent</TableHead>
            <TableHead>Sport</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Prediction</TableHead>
            <TableHead>Odds</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bets.map((bet) => {
            const persona = personas.find(
              (p) => p.persona_id === bet.persona_id,
            );
            const personaName = persona ? persona.name : "Unknown Agent";

            // Get enhanced game info if available from validation
            const enhancedGame =
              bet.validation_result?.bet_updates?.game || bet.game;

            // Check if the bet has been validated with sources
            const hasSources =
              bet.validation_result?.sources &&
              bet.validation_result.sources.length > 0;

            return (
              <TableRow key={bet.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center">
                    {persona && (
                      <PersonaAvatar
                        persona={persona}
                        className="mr-2 w-5 h-5"
                      />
                    )}
                    <span className="truncate max-w-24">{personaName}</span>
                  </div>
                </TableCell>
                <TableCell>{bet.sport}</TableCell>
                <TableCell className="max-w-32 truncate">
                  <div className="flex items-center gap-1">
                    {enhancedGame}
                    {hasSources && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <CheckCircle className="h-3 w-3 text-green-500" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">Verified with sources</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </TableCell>
                <TableCell className="max-w-32 truncate">
                  {bet.prediction}
                </TableCell>
                <TableCell>{bet.odds}</TableCell>
                <TableCell>{formatDate(bet.created_at)}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(bet.status)}
                    {getValidationBadge(bet.validation_status)}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {bet.status === "pending" ? (
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        className="h-7 text-green-500 hover:text-green-400 hover:bg-green-500/10"
                        onClick={() => handleUpdateResult(bet, "won")}
                      >
                        Win
                      </Button>
                      <Button
                        variant="ghost"
                        className="h-7 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                        onClick={() => handleUpdateResult(bet, "lost")}
                      >
                        Loss
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setSelectedBet(bet);
                        setShowDetailsModal(true);
                      }}
                    >
                      Details
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Result Update Modal */}
      {selectedBet && (
        <UpdateResultModal
          isOpen={showResultModal}
          onClose={() => {
            setShowResultModal(false);
            setSelectedBet(null);
          }}
          onSubmit={markBetAs}
          status={resultAction}
          bet={selectedBet}
        />
      )}

      {/* Bet Details Modal */}
      {selectedBet && (
        <BetDetailsModal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedBet(null);
          }}
          bet={selectedBet}
          persona={personas.find(
            (p) => p.persona_id === selectedBet.persona_id,
          )}
          onMarkAsWon={() => {
            setShowDetailsModal(false);
            setTimeout(() => handleUpdateResult(selectedBet, "won"), 100);
          }}
          onMarkAsLost={() => {
            setShowDetailsModal(false);
            setTimeout(() => handleUpdateResult(selectedBet, "lost"), 100);
          }}
        />
      )}
    </div>
  );
};

export default BetTable;
